import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { PlaneTakeoff, MapPin, CalendarDays, PartyPopper, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { Spinner } from "../components/common";
import { SITE } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* PUBLIC trip countdown widget — shareable + embeddable (?embed=1 hides chrome). */

export default function CountdownWidget() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [data, setData] = useState(undefined);

  useEffect(() => {
    axios.get(`${API}/embed/countdown/${token}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [token]);

  if (data === undefined) return <Spinner full />;
  if (data === null) return (
    <div className="max-w-md mx-auto px-5 py-24 text-center" data-testid="countdown-dead">
      <p className="font-heading font-700 text-xl">This countdown is no longer active</p>
      <p className="text-sm text-muted-foreground mt-2">The trip owner may have turned it off.</p>
    </div>
  );

  const dates = [data.start_date, data.end_date].filter(Boolean).join(" – ");
  return (
    <div className={`max-w-md mx-auto px-4 ${embed ? "py-3" : "py-12"}`} data-testid="countdown-widget">
      <div className="border border-zinc-300 bg-indigo-950 text-white overflow-hidden">
        <div className="px-6 pt-6 text-center">
          <div className="text-[11px] font-mono uppercase tracking-widest text-blue-300 flex items-center justify-center gap-1.5">
            <PlaneTakeoff className="w-3.5 h-3.5" /> Trip countdown
          </div>
          <h1 className="font-heading font-900 text-2xl mt-2" data-testid="countdown-title">{data.title}</h1>
          {data.stops?.length > 0 && (
            <div className="text-xs text-white/70 mt-1 flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" /> {data.stops.join(" → ")}
            </div>
          )}
        </div>
        <div className="px-6 py-6 text-center">
          {data.state === "upcoming" && (
            <div data-testid="countdown-upcoming" aria-label={`${data.days_to_go} days to go until ${data.title}`}>
              <div className="font-heading font-900 text-6xl text-amber-400 leading-none">{data.days_to_go}</div>
              <div className="text-sm font-700 uppercase tracking-widest text-white/80 mt-1.5">
                {" "}day{data.days_to_go === 1 ? "" : "s"} to go
              </div>
            </div>
          )}
          {data.state === "active" && (
            <div data-testid="countdown-active">
              <PartyPopper className="w-8 h-8 mx-auto text-amber-400" />
              <div className="font-heading font-900 text-3xl mt-2">We're travelling!</div>
              <div className="text-sm text-white/80 mt-1">Day {data.day_n} of {data.total_days}</div>
            </div>
          )}
          {data.state === "done" && (
            <div data-testid="countdown-done">
              <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-400" />
              <div className="font-heading font-900 text-2xl mt-2">Trip complete</div>
              <div className="text-sm text-white/70 mt-1">What a ride — see you on the next one!</div>
            </div>
          )}
          <div className="text-xs text-white/60 mt-4 flex items-center justify-center gap-1.5">
            <CalendarDays className="w-3.5 h-3.5" /> {dates}{data.shared_by ? ` · by ${data.shared_by}` : ""}
          </div>
        </div>
        <a href={SITE.url || "https://travelerpulse.pro"} target="_blank" rel="noreferrer"
          className="block bg-white/10 text-center text-[11px] font-700 text-blue-200 py-2 hover:bg-white/15 transition-colors">
          Plan your own trip with {SITE.name || "TravelerPulse"} →
        </a>
      </div>
    </div>
  );
}
